import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@powercore/ui';
import { Download, Sparkles, ArrowRight } from 'lucide-react';
import { api, type SkillSummary } from '../lib/api';

const TOOL_LABELS: Record<string, string> = {
  claude_code: 'Claude Code', chatgpt: 'ChatGPT', codex: 'Codex', cursor: 'Cursor', gemini: 'Gemini',
};
const font = { fontFamily: "'Outfit', sans-serif" };

export function MySkillsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMySkills().then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (Array.isArray(data)) setSkills(data);
      setLoading(false);
    });
  }, []);

  const download = async (slug: string) => {
    try {
      await api.downloadSkill(slug);
      addToast({ title: 'Downloading', description: `${slug}.zip`, variant: 'success' });
    } catch {
      addToast({ title: 'Download failed', variant: 'error' });
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <h1 className="text-2xl font-semibold text-gray-900" style={font}>My Skills</h1>
      <p className="mt-1 text-gray-600" style={font}>Everything you've unlocked — install into any tool, anytime.</p>

      {loading ? (
        <div className="py-20 text-center text-gray-400" style={font}>Loading…</div>
      ) : skills.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm">✨</div>
          <h2 className="text-lg font-semibold text-gray-900" style={font}>No skills yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500" style={font}>Browse the store and unlock your first skill — the Public Communication skill is a great place to start.</p>
          <button onClick={() => navigate('/')} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700" style={font}>
            <Sparkles className="h-4 w-4" /> Browse the store
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {skills.map((s) => (
            <div key={s.id} className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 text-xl">{s.emoji || '✨'}</div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-gray-900" style={font}>{s.name}</h3>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(s.compatible_tools || []).slice(0, 3).map((t) => (
                    <span key={t} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">{TOOL_LABELS[t] || t}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                <button onClick={() => download(s.slug)} className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900" title="Download .zip"><Download className="h-4 w-4" /></button>
                <button onClick={() => navigate(`/skills/${s.slug}`)} className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900" title="Open"><ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MySkillsPage;
